/**
 * RichTextarea -- textarea + highlight overlay
 *
 * The real <textarea> receives all input (rendered with transparent text).
 * The overlay <div> renders the same content with mention segments styled
 * as chips. This avoids contentEditable complexity while keeping native
 * undo/paste/IME support.
 */

import {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { cn } from '@/lib/utils'
import { MentionChip } from '@/components/polix-ai/mention-chip'
import type { InputSegment, MentionEntity } from '@/types/rich-input'

export interface RichTextareaHandle {
  focus: () => void
  getTextareaElement: () => HTMLTextAreaElement | null
}

interface RichTextareaProps {
  readonly segments: readonly InputSegment[]
  readonly rawText: string
  readonly onChange: (text: string) => void
  readonly onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  readonly onSelect: () => void
  readonly disabled?: boolean
  readonly placeholder?: string
  readonly className?: string
  readonly onMentionRemove?: (entity: MentionEntity) => void
}

export const RichTextarea = forwardRef<RichTextareaHandle, RichTextareaProps>(
  function RichTextarea(
    {
      segments,
      rawText,
      onChange,
      onKeyDown,
      onSelect,
      disabled = false,
      placeholder = 'Type your message...',
      className,
      onMentionRemove,
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const overlayRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      getTextareaElement: () => textareaRef.current,
    }))

    // Sync scroll between textarea and overlay
    const syncScroll = useCallback(() => {
      if (textareaRef.current && overlayRef.current) {
        overlayRef.current.scrollTop = textareaRef.current.scrollTop
        overlayRef.current.scrollLeft = textareaRef.current.scrollLeft
      }
    }, [])

    // Auto-expand textarea
    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'

        const lineHeight = 24
        const padding = 16
        const maxHeight = lineHeight * 10 + padding
        const scrollHeight = textareaRef.current.scrollHeight

        if (scrollHeight > maxHeight) {
          textareaRef.current.style.height = `${maxHeight}px`
          textareaRef.current.style.overflowY = 'auto'
        } else {
          textareaRef.current.style.height = `${scrollHeight}px`
          textareaRef.current.style.overflowY = 'hidden'
        }
      }
    }, [rawText])

    // Build overlay content
    const overlayContent = segments.map((segment, idx) => {
      if (segment.type === 'text') {
        // Preserve whitespace including trailing spaces and newlines
        return (
          <span key={idx} className="whitespace-pre-wrap">
            {segment.text}
          </span>
        )
      }

      return (
        <MentionChip
          key={`mention-${segment.entity.id}-${idx}`}
          entity={segment.entity}
          onRemove={
            onMentionRemove
              ? () => onMentionRemove(segment.entity)
              : undefined
          }
        />
      )
    })

    return (
      <div className={cn('relative', className)}>
        {/* Highlight overlay -- rendered text with styled mentions */}
        <div
          ref={overlayRef}
          aria-hidden="true"
          className={cn(
            'absolute inset-0 pointer-events-none overflow-hidden',
            'whitespace-pre-wrap break-words',
            'px-3 py-2 text-sm',
            // Same font metrics as the textarea
            'font-[inherit] leading-[inherit]'
          )}
        >
          {segments.length > 0 ? (
            overlayContent
          ) : (
            <span className="text-transparent">{placeholder}</span>
          )}
        </div>

        {/* Real textarea -- captures all input */}
        <textarea
          ref={textareaRef}
          value={rawText}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onSelect={onSelect}
          onScroll={syncScroll}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            'relative z-10 w-full resize-none bg-transparent',
            'px-3 py-2 text-sm',
            // Text is transparent so the overlay shows through
            'text-transparent caret-foreground',
            'outline-none',
            'min-h-[60px] max-h-[256px]',
            'transition-[border-color,box-shadow] duration-200',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-label="Message input"
        />
      </div>
    )
  }
)
